import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Layout from "@/components/Layout";
import PixelInjector from "@/components/PixelInjector";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "@/components/ui/toaster";

import Home from "@/pages/Home";
import CheckerLanding from "@/pages/CheckerLanding";
import ChecksHub from "@/pages/ChecksHub";
import CheckBrand from "@/pages/CheckBrand";
import ThemeIntent from "@/pages/ThemeIntent";
import HtmlSitemap from "@/pages/HtmlSitemap";
import AnalyseFlow from "@/pages/AnalyseFlow";
import Result from "@/pages/Result";
import Faq from "@/pages/Faq";
import LegalPage from "@/pages/LegalPage";
import HoeHetWerkt from "@/pages/HoeHetWerkt";
import Prijzen from "@/pages/Prijzen";
import OverOns from "@/pages/OverOns";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import VoorBedrijven from "@/pages/VoorBedrijven";
import VoorBedrijvenAanmelden from "@/pages/VoorBedrijvenAanmelden";
import VoorBedrijvenBedankt from "@/pages/VoorBedrijvenBedankt";
import VoorBedrijvenProfiel from "@/pages/VoorBedrijvenProfiel";
import BetalingSucces from "@/pages/BetalingSucces";
import BetalingMislukt from "@/pages/BetalingMislukt";
import Credits from "@/pages/Credits";
import MijnChecks from "@/pages/MijnChecks";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminChecks from "@/pages/admin/AdminChecks";
import AdminRevenue from "@/pages/admin/AdminRevenue";
import AdminRefunds from "@/pages/admin/AdminRefunds";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminDiscountCodes from "@/pages/admin/AdminDiscountCodes";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminAutoBlog from "@/pages/admin/AdminAutoBlog";
import AdminLegalRules from "@/pages/admin/AdminLegalRules";
import AdminLegalPages from "@/pages/admin/AdminLegalPages";
import AdminEmailTemplates from "@/pages/admin/AdminEmailTemplates";
import AdminConfig from "@/pages/admin/AdminConfig";
import AdminSeo from "@/pages/admin/AdminSeo";
import AdminSettings from "@/pages/admin/AdminSettings";

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Home />} />
            <Route path="/checks" element={<ChecksHub />} />
            <Route path="/checks/:slug" element={<CheckBrand />} />
            <Route path="/sitemap" element={<HtmlSitemap />} />
            <Route path="/check/:slug" element={<CheckerLanding />} />
            <Route path="/check/:slug/analyse" element={<AnalyseFlow />} />
            <Route path="/result/:id" element={<Result />} />
            <Route path="/rapport/:id" element={<Result />} />

            {/* SEO Intent-pagina's: /{thema}-controleren — expliciet om route-conflicten te vermijden */}
            <Route path="/huurcontract-controleren" element={<ThemeIntent />} />
            <Route path="/verzekeringspolis-controleren" element={<ThemeIntent />} />
            <Route path="/loonstrook-controleren" element={<ThemeIntent />} />
            <Route path="/arbeidscontract-controleren" element={<ThemeIntent />} />
            <Route path="/energiecontract-controleren" element={<ThemeIntent />} />
            <Route path="/kredietcontract-controleren" element={<ThemeIntent />} />
            <Route path="/garantievoorwaarden-controleren" element={<ThemeIntent />} />
            <Route path="/ingebrekestelling-controleren" element={<ThemeIntent />} />
            <Route path="/algemene-voorwaarden-controleren" element={<ThemeIntent />} />
            <Route path="/bouwverslag-controleren" element={<ThemeIntent />} />
            <Route path="/aankoopakte-controleren" element={<ThemeIntent />} />
            <Route path="/pensioenfiche-controleren" element={<ThemeIntent />} />
            <Route path="/niet-concurrentiebeding-controleren" element={<ThemeIntent />} />
            <Route path="/echtscheidingsakkoord-controleren" element={<ThemeIntent />} />
            <Route path="/kinderopvangcontract-controleren" element={<ThemeIntent />} />
            <Route path="/influencercontract-controleren" element={<ThemeIntent />} />
            <Route path="/zorgcontract-controleren" element={<ThemeIntent />} />
            <Route path="/hypotheek-controleren" element={<ThemeIntent />} />
            <Route path="/app-privacy-controleren" element={<ThemeIntent />} />
            <Route path="/bankafschrift-controleren" element={<ThemeIntent />} />
            <Route path="/phishing-mail-controleren" element={<ThemeIntent />} />
            <Route path="/factuur-controleren" element={<ThemeIntent />} />
            <Route path="/auto-aankoop-controleren" element={<ThemeIntent />} />
            <Route path="/contracten-vergelijken" element={<ThemeIntent />} />
            <Route path="/document-laten-controleren" element={<ThemeIntent />} />

            <Route path="/hoe-het-werkt" element={<HoeHetWerkt />} />
            <Route path="/prijzen" element={<Prijzen />} />
            <Route path="/over-ons" element={<OverOns />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/credits" element={<Credits />} />
            <Route path="/mijn-checks" element={<MijnChecks />} />

            <Route path="/faq" element={<Faq />} />
            <Route path="/privacy" element={<LegalPage slug="privacy" />} />
            <Route path="/algemene-voorwaarden" element={<LegalPage slug="algemene-voorwaarden" />} />
            <Route path="/cookiebeleid" element={<LegalPage slug="cookiebeleid" />} />

            <Route path="/voor-bedrijven" element={<VoorBedrijven />} />
            <Route path="/voor-bedrijven/aanmelden" element={<VoorBedrijvenAanmelden />} />
            <Route path="/voor-bedrijven/bedankt" element={<VoorBedrijvenBedankt />} />
            <Route path="/voor-bedrijven/:slug" element={<VoorBedrijvenProfiel />} />

            <Route path="/betaling-succes" element={<BetalingSucces />} />
            <Route path="/betaling-mislukt" element={<BetalingMislukt />} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/checks" element={<AdminChecks />} />
            <Route path="/admin/revenue" element={<AdminRevenue />} />
            <Route path="/admin/refunds" element={<AdminRefunds />} />
            <Route path="/admin/businesses" element={<AdminBusinesses />} />
            <Route path="/admin/discount-codes" element={<AdminDiscountCodes />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/admin/auto-blog" element={<AdminAutoBlog />} />
            <Route path="/admin/legal-rules" element={<AdminLegalRules />} />
            <Route path="/admin/legal-pages" element={<AdminLegalPages />} />
            <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
            <Route path="/admin/config" element={<AdminConfig />} />
            <Route path="/admin/seo" element={<AdminSeo />} />
            <Route path="/admin/settings" element={<AdminSettings />} />

            <Route path="*" element={<Home />} />
          </Routes>
        </Layout>
        <PixelInjector />
        <Toaster />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
